import type { Rect } from "@/libs/geometry/types"
import { type ImageSource, getImageSourceDimensions, isVideoFrame } from "@/libs/image"
import { clamp } from "@/libs/math"

export const createCanvas = (width = 300, height = 150) => {
  /*
   * NOTE:
   * @mediapipe/task-vision crashes on Samsung Internet Browser v20+ (LIQ-70)
   * if HTMLCanvasElement is used, but it works well if OffscreenCanvas is used.
   * So, try to use OffscreenCanvas if it is supported for all the CV-related things.
   */
  if (isOffscreenCanvasSupported()) return new OffscreenCanvas(width, height)

  return Object.assign(document.createElement("canvas"), {
    width,
    height,
    // Compatibility with OffscreenCanvas.convertToBlob
    convertToBlob(options?: ImageEncodeOptions) {
      const canvas = this as unknown as HTMLCanvasElement

      return new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          (blob) =>
            blob
              ? resolve(blob)
              : reject(
                  new Error(
                    [
                      "Error while converting canvas to",
                      options?.type ? `"${options.type}"` : "",
                      "blob",
                    ]
                      .filter(Boolean)
                      .join(" "),
                  ),
                ),
          options?.type,
          options?.quality,
        ),
      )
    },
  })
}
/** Performs a set of actions to free up the memory taken by the canvas */
export const destroyCanvas = (canvas: HTMLCanvasElement | OffscreenCanvas) => {
  // Safari-specific fix: https://pqina.nl/blog/total-canvas-memory-use-exceeds-the-maximum-limit/
  canvas.width = canvas.height = 1
}

export const isCanvasImageSource = (
  source: unknown,
): source is HTMLCanvasElement | OffscreenCanvas => {
  if (source instanceof HTMLCanvasElement) return true

  return typeof OffscreenCanvas !== "undefined" && source instanceof OffscreenCanvas
}

export const resizeCanvas = ({
  width,
  height,
  canvas,
  gl,
  ignoreDevicePixelRatio,
}: {
  canvas: HTMLCanvasElement
  width: number
  height: number
  gl?: WebGL2RenderingContext | null
  ignoreDevicePixelRatio?: boolean
}) => {
  canvas.width = width
  canvas.height = height

  const multiplier = ignoreDevicePixelRatio ? 1 : clamp(window.devicePixelRatio, { min: 1 })
  const w = clamp(Math.floor(canvas.clientWidth * multiplier), { min: 1 })
  const h = clamp(Math.floor(canvas.clientHeight * multiplier), { min: 1 })

  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w
    canvas.height = h
  }

  if (gl) gl.viewport(0, 0, w, h)
}

let _isOffscreenCanvasSupported: boolean

const isOffscreenCanvasSupported = () => {
  if (typeof _isOffscreenCanvasSupported === "boolean") return _isOffscreenCanvasSupported
  if (typeof OffscreenCanvas === "undefined") return (_isOffscreenCanvasSupported = false)

  /*
   * NOTE:
   * Safari 16.4.1 has implemented OffscreenCanvas with only 2D context available.
   * Such OffscreenCanvas implementation is considered incomplete and full support of OffscreenCanvas is considered unachievable.
   * Furthermore, the incomplete OffscreenCanvas implementation leads to a bug that the OffscreenCanvas can not be renderer to a WebGL texture by gl.texImage2D.
   */
  return (_isOffscreenCanvasSupported = new OffscreenCanvas(1, 1).getContext("webgl2") !== null)
}

/**
 * Draws the given image or, if specified, a `rect` of the image to the canvas.
 * Canvas size is automatically adjusted to the rendered image size.
 */
export const drawImage = (
  canvas: HTMLCanvasElement | OffscreenCanvas,
  image: ImageSource,
  options: {
    rect?: Rect
    horizontalFlip?: boolean
  } = {},
) => {
  const {
    x = 0,
    y = 0,
    width,
    height,
  } = options?.rect ? options.rect : (getImageSourceDimensions(image) as Rect)

  if (canvas.width !== width) canvas.width = width
  if (canvas.height !== height) canvas.height = height

  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D

  let sx = x
  let sy = y
  let sw = width
  let sh = height

  if (isVideoFrame(image)) {
    const scale = image.visibleRect!.width / image.displayWidth
    sx *= scale
    sy *= scale
    sw *= scale
    sh *= scale
  }

  if (options?.horizontalFlip) ctx.translate(canvas.width, 0), ctx.scale(-1, 1)

  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)
}
